import { Box, CircularProgress, Grid, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getCardDataMemberApi, getCardDataStatusApi, updateCardDataMemberApi } from '../../api/cardDataApi';

import { BasicButton } from '../../shared/basicButton';
import { BasicTextBox } from '../../shared/basicTextBox';
import { CardDataStatusItem, CardMemberDataItem, CardMemberItem } from '../../api/models/cardData';
import { useApiCall } from '../../hooks/useApiCall';
import { BasicDropDown } from '../../shared/basicDropDown';
import { gradeClassList, heightFeetList, heightInchesList } from '../../models/dropDownItemViewModel';

import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import { BasicDatePicker } from '../../shared/basicDatePicker';

export function CardDataFormPage() {
	const [pin, setPin] = useState<string | undefined>(undefined);
	const [status, setStatus] = useState<CardDataStatusItem | undefined>(undefined);
	const [memberData, setMemberData] = useState<CardMemberDataItem | undefined>(undefined);

	const [nickName, setNickName] = useState<string>('');
	const [school, setSchool] = useState<string>('');
	const [seasonsExp, setSeasonsExp] = useState<string>('');
	const [position, setPosition] = useState<string>('');
	const [jerseyNumber, setJerseyNumber] = useState<string>('');
	const [heightFeet, setHeightFeet] = useState<string>('');
	const [heightInches, setHeightInches] = useState<string>('');

	const [weight, setWeight] = useState<string>('');
	const [birthDate, setBirthDate] = useState<string>('');
	const [birthPlace, setBirthPlace] = useState<string>('');
	const [hobby, setHobby] = useState<string>('');
	const [favTeam, setFavTeam] = useState<string>('');
	const [favSubject, setFavSubject] = useState<string>('');
	const [dreamJob, setDreamJob] = useState<string>('');
	const [uniqueFact, setUniqueFact] = useState<string>('');
	const [gradeClass, setGradeClass] = useState<string>('');

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [resultMessage, setResultMessage] = useState<string>('');

	const params = useParams();

	const getCardDataStatusApiRequest = useApiCall(getCardDataStatusApi);
	const getMemberApiRequest = useApiCall(getCardDataMemberApi);
	const updateMemberApiRequest = useApiCall(updateCardDataMemberApi);

	// Retrieve status data that indicates if the form can be viewed or edited
	useEffect(() => {
		if (!params.secCode) {
			//todo: display error or redirect to home page
		}
		fetchStatus();
	}, []);

	//set page status that indicates if the form can be viewed or edited
	async function fetchStatus() {
		const response = await getCardDataStatusApiRequest.request(params.secCode);
		setStatus(response);
	}

	// Retrieve member data if the status indicates it can be viewed
	async function getMemberData() {
		if (!params.secCode || !pin) {
			return;
		}

		setIsLoading(true);
		setResultMessage(``);

		try {
			const response = await getMemberApiRequest.request(params.secCode, pin);
			if (response) {
				initMemberData(response);
			}
		} catch (error: any) {
			setResultMessage(`Error: ${error.message}`);
		}

		setIsLoading(false);
	}

	async function initMemberData(memberDataItem: CardMemberDataItem) {
		setMemberData(memberDataItem);

		if (memberDataItem) {
			setNickName(memberDataItem.member.nickName ?? '');
			setSchool(memberDataItem.member.school ?? '');
			setSeasonsExp(memberDataItem.member.seasonsExp?.toString() ?? '');
			setPosition(memberDataItem.member.position ?? '');
			setJerseyNumber(memberDataItem.member.jerseyNumber?.toString() ?? '');
			setHeightFeet('');
			setHeightInches('');

			setWeight(memberDataItem.member.weight?.toString() ?? '');
			setBirthPlace(memberDataItem.member.birthplace ?? '');
			setHobby(memberDataItem.member.hobby ?? '');
			setFavTeam(memberDataItem.member.favoriteTeam ?? '');
			setFavSubject(memberDataItem.member.favoriteSubject ?? '');
			setDreamJob(memberDataItem.member.occupation ?? '');
			setUniqueFact(memberDataItem.member.uniqueFact ?? '');
			setGradeClass(memberDataItem.member.gradeClass ?? '');
			setBirthDate(
				memberDataItem.member.birthDate ? dayjs(memberDataItem.member.birthDate).format('YYYY-MM-DD') : ''
			);

			if (memberDataItem.member.height) {
				const totalHeightInInches = memberDataItem.member.height;
				const xHeightFeet = Math.floor(totalHeightInInches / 12);
				const xHeightInches = totalHeightInInches % 12;

				setHeightFeet(xHeightFeet.toString());
				setHeightInches(xHeightInches.toString());
			}
		}
	}

	function updatePositionHandler(newPosition: string) {
		setPosition(newPosition.toUpperCase());
	}

	async function updateData() {

		let height='';

        if (heightFeet && heightInches) {
			height = (parseInt(heightFeet) * 12 + parseInt(heightInches)).toString();
		}

		const updateCommand = {
			nickName,
			seasonsExp,
			school,
			birthDate: birthDate,
			height,
			weight,
			birthplace: birthPlace,
			hobby,
			favoriteTeam: favTeam,
			position: position,
			jerseyNumber: jerseyNumber,
			favoriteSubject: favSubject,
			occupation: dreamJob,
			uniqueFact,
			gradeClass,
		};

		setIsLoading(true);
		setResultMessage('');
		try {
			await updateMemberApiRequest.request(params.secCode, pin, updateCommand);
			setResultMessage(`Updated!`);
		} catch (error: any) {
			console.error('Error updating member data!:', error);
			setResultMessage(`Error: ${error.message}`);
			console.error('Error updating member data:', error);
		}

		setIsLoading(false);
	}

	function getPinDisplay() {
		const pinRefs = Array.from({ length: 4 }, () => React.createRef<HTMLInputElement>());
		return (
			<Box>
				<h3>Enter PIN</h3>
				<p>PIN can be a letter or number.</p>
				<Box display="flex" flexDirection="row" flexWrap="nowrap" gap={2} justifyContent="center" overflow="auto">
					{[0, 1, 2, 3].map((idx) => (
						<TextField
							key={idx}
							id={`pin-char-${idx}`}
							value={pin?.[idx] ?? ''}
							inputRef={pinRefs[idx]}
							onChange={(e) => {
								const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 1);
								let newPin = pin ? pin.split('') : ['', '', '', ''];
								newPin[idx] = val;
								setPin(newPin.join(''));
								if (val && idx < pinRefs.length - 1) {
									pinRefs[idx + 1].current?.focus();
								}
							}}
							inputProps={{
								maxLength: 1,
								style: {
									fontSize: 32,
									textAlign: 'center',
									width: 48,
									height: 56,
									letterSpacing: 2,
								},
								autoComplete: 'off',
								inputMode: 'text',
							}}
						/>
					))}
				</Box>
				<Box mt={2} display="flex">
					<BasicButton onClick={getMemberData} labelText="Submit PIN" />
				</Box>
			</Box>
		);
	}

	function mainContent() {
		//todo: jerseyText needs to implement padding
		const jerseyText = memberData?.member?.jerseyNumber ? `#${memberData?.member?.jerseyNumber}` : '';
		const positionText = memberData?.member?.position ? ` - ${memberData?.member?.position}` : '';

		return (
			<Box>
				<br />
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<img src={memberData?.member?.orgSeason?.logoLink} height={50} alt="profile" />
					{memberData?.member?.orgSeason && (
						<h2 style={{ margin: '0 10px' }}>{memberData?.member?.orgSeason.name}</h2>
					)}
				</div>

				<h1>{`${memberData?.member?.firstName} ${memberData?.member?.lastName}`}</h1>

				<div style={{ display: 'flex', gap: 16 }}>
					<img src={memberData?.member?.profileLink} height={150} alt="profile" />
					<img src={memberData?.member?.actionLink} height={150} alt="action" />
				</div>

				{memberData?.member?.team && (
					<h3 style={{ margin: '0 10px' }}>
						{memberData?.member?.team.name} {jerseyText} {positionText}
					</h3>
				)}
				{memberData?.canEdit && memberData.editExpirationDate && (
					<p style={{ color: 'maroon', fontWeight: '500' }}>
						You will not be able to make changes after {dayjs(memberData.editExpirationDate).format('MM/DD/YY')}
					</p>
				)}
				<p>
					email any photos to <a href="mailto:info@colbrensports.com">info@colbrensports.com</a>
				</p>
				<br />

				<Grid container direction="column" spacing={2}>
					<Grid item>
						{status && status.canEdit && (
							<BasicTextBox
								id="txtNickName"
								label="NickName"
								maxLength={20}
								width={275}
								value={nickName}
								onChange={(e) => setNickName(e.target.value)}
							/>
						)}
						{status && !status.canEdit && (
							<p>
								NickName: <span className="field-value">{nickName}</span>
							</p>
						)}
					</Grid>

					{!memberData?.member.isLeader && (
						<Grid item>
							{status && status.canEdit && (
								<BasicDatePicker
									width={250}
									label="Birthdate"
									value={birthDate}
									onChange={(date) => setBirthDate(date ? date : '')}
								/>
							)}

							{status && !status.canEdit && (
								<p>
									Birthdate: <span className="field-value">{birthDate}</span>
								</p>
							)}
						</Grid>
					)}

					{!memberData?.member.isLeader && (
						<Grid item>
							{status && status.canEdit && (
								<BasicTextBox
									id="txtPosition"
									label="Position"
									maxLength={3}
									width={275}
									value={position}
									onChange={(e) => updatePositionHandler(e.target.value)}
								/>
							)}
							{status && !status.canEdit && (
								<p>
									Position: <span className="field-value">{position}</span>
								</p>
							)}
						</Grid>
					)}

					<Grid item>
						{status && status.canEdit && (
							<BasicTextBox
								id="txtSchool"
								label="School"
								maxLength={20}
								width={275}
								value={school}
								onChange={(e) => setSchool(e.target.value)}
							/>
						)}
						{status && !status.canEdit && (
							<p>
								School: <span className="field-value">{school}</span>
							</p>
						)}
					</Grid>

					{!memberData?.member.isLeader && (
						<Grid item>
							{status && status.canEdit && (
								<BasicDropDown
									id="gradeClass"
									label="select grade"
									width={275}
									value={gradeClass}
									items={gradeClassList}
									onChange={(e) => setGradeClass(e.target.value)}
								/>
							)}

							{status && !status.canEdit && (
								<p>
									Grade: <span className="field-value">{gradeClass}</span>
								</p>
							)}
						</Grid>
					)}

					{!memberData?.member.isLeader && (
						<Grid item>
							{status && status.canEdit && (
								<BasicTextBox
									id="txtJerseyNumber"
									label="JerseyNumber"
									maxLength={20}
									width={275}
									value={jerseyNumber}
									onChange={(e) => setJerseyNumber(e.target.value)}
								/>
							)}
							{status && !status.canEdit && (
								<p>
									Jersey Number: <span className="field-value">{jerseyNumber}</span>
								</p>
							)}
						</Grid>
					)}

					{!memberData?.member.isLeader && (
						<Grid item>
							{status && status.canEdit && (
								<BasicTextBox
									id="txtSeasonsExp"
									label="Number Seasons Exp"
									maxLength={20}
									width={275}
									value={seasonsExp}
									onChange={(e) => setSeasonsExp(e.target.value)}
								/>
							)}
							{status && !status.canEdit && (
								<p>
									Number Seasons Exp: <span className="field-value">{seasonsExp}</span>
								</p>
							)}
						</Grid>
					)}

					{!memberData?.member.isLeader && (
						<Grid item>
							{status && status.canEdit && (
								<Box display="flex" alignItems="center" gap={1}>
									<BasicDropDown
										id="heightFeet"
										label="Height (Feet)"
										width={120}
										value={heightFeet}
										items={heightFeetList}
										onChange={(e) => setHeightFeet(e.target.value)}
									/>
									<span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>-</span>
									<BasicDropDown
										id="heightInches"
										label="Height (Inches)"
										width={120}
										value={heightInches}
										items={heightInchesList}
										onChange={(e) => setHeightInches(e.target.value)}
									/>
								</Box>
							)}

							{status && !status.canEdit && (
								<p>
									Height:{' '}
									<span className="field-value">
										{heightFeet} ft - {heightInches} in
									</span>
								</p>
							)}
						</Grid>
					)}

					{!memberData?.member.isLeader && (
						<Grid item>
							{status && status.canEdit && (
								<BasicTextBox
									id="txtWeight"
									label="Weight"
									maxLength={20}
									width={275}
									value={weight}
									onChange={(e) => setWeight(e.target.value)}
								/>
							)}
							{status && !status.canEdit && (
								<p>
									Weight: <span className="field-value">{weight}</span>
								</p>
							)}
						</Grid>
					)}

					{/* <Grid item>
						{status && status.canEdit && (
							<BasicTextBox
								id="txtBirthPlace"
								label="BirthPlace"
								maxLength={20}
								width={275}
								value={birthPlace}
								onChange={(e) => setBirthPlace(e.target.value)}
							/>
						)}
						{status && !status.canEdit && <p>Birth Place: <span className="field-value">{birthPlace}</span></p>}
					</Grid> */}

					<Grid item>
						{status && status.canEdit && (
							<BasicTextBox
								id="txtHobby"
								label="Hobby"
								maxLength={20}
								width={275}
								value={hobby}
								onChange={(e) => setHobby(e.target.value)}
							/>
						)}
						{status && !status.canEdit && (
							<p>
								Hobby: <span className="field-value">{hobby}</span>
							</p>
						)}
					</Grid>

					<Grid item>
						{status && status.canEdit && (
							<BasicTextBox
								id="txtFavTeam"
								label="Favorite Team"
								maxLength={20}
								width={275}
								value={favTeam}
								onChange={(e) => setFavTeam(e.target.value)}
							/>
						)}
						{status && !status.canEdit && (
							<p>
								Favorite Team: <span className="field-value">{favTeam}</span>
							</p>
						)}
					</Grid>

					<Grid item>
						{status && status.canEdit && (
							<BasicTextBox
								id="txtFavSubject"
								label="Favorite Subject"
								maxLength={20}
								width={275}
								value={favSubject}
								onChange={(e) => setFavSubject(e.target.value)}
							/>
						)}

						{status && !status.canEdit && (
							<p>
								Favorite Subject: <span className="field-value">{favSubject}</span>
							</p>
						)}
					</Grid>

					<Grid item>
						{status && status.canEdit && (
							<BasicTextBox
								id="txtDreamJob"
								label="Dream Job"
								maxLength={20}
								width={275}
								value={dreamJob}
								onChange={(e) => setDreamJob(e.target.value)}
							/>
						)}

						{status && !status.canEdit && (
							<p>
								Dream Job: <span className="field-value">{dreamJob}</span>
							</p>
						)}
					</Grid>

					<Grid item>
						{status && status.canEdit && (
							<BasicTextBox
								id="txtUniqueFact"
								label="UniqueFact"
								maxLength={55}
								width={500}
								value={uniqueFact}
								onChange={(e) => setUniqueFact(e.target.value)}
							/>
						)}

						{status && !status.canEdit && (
							<p>
								Unique Fact: <span className="field-value">{uniqueFact}</span>
							</p>
						)}
					</Grid>

					<Grid item>
						{status && status.canEdit && !isLoading && <BasicButton onClick={updateData} labelText="Update" />}
					</Grid>
				</Grid>
			</Box>
		);
	}

	return (
		<Box display="flex" justifyContent="center" alignItems="flex-start" minHeight="100vh" px={2}>
			<Box width="100%" maxWidth="400px">
				{!status && <CircularProgress />}
				{status && !status.canView && <h3>This page is not configured to be viewed</h3>}

				{status && status.canView && !memberData && getPinDisplay()}
				{memberData && mainContent()}
				{isLoading && <CircularProgress />}
				{resultMessage && <p>{resultMessage}</p>}
			</Box>
		</Box>
	);
}
